import { Component, OnInit } from '@angular/core';
import { IRosterCode } from '../shared/models/roster-code';
import { IRosterValue } from "../shared/models/roster/roster-value";
import { IEmployeeRosterUpdateModel } from "../shared/models/roster/employee-roster-update-model";
import { NgForm } from '@angular/forms';
import { RosterCodes } from '../shared/models/roster-codes';
import { RosterPatternParser } from '../services/roster-pattern-parser';
import { IApiResult } from '../shared/models/api-result';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PpsUtilityService } from '../services/pps-utility.service';
import { RosterTypeService } from '../services/roster-type.service';
import { IRosterNote } from '../shared/models/roster/roster-note';
import { TicketStatus } from '../shared/models/roster/ticket-status';
import * as _ from 'lodash-es';
import { isSameDay } from 'date-fns';
import { PayrollRosterCodeRecommendor } from './payroll-roster-code-recommendor';
import { RosterCodeService } from '../services/roster-code.service';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'pps-employee-roster-form',
  templateUrl: './employee-roster-form.component.html',
  styleUrls: ['./employee-roster-form.component.css']
})
export class EmployeeRosterFormComponent implements OnInit{
  employeeName:string = "";
  errorMessage: string = "";

  rosterPattern: string = "FIP DAY 1:13 FOP R&R 6 FIP NIGHT 2:13 FOA R&R 6";
  showRosterNoteField: boolean = false;
  
  cycles: number = 1;

  rosterDate: Date;
  rosterCodes: IRosterCode[] = [];
  rosterCodesObject: {[id: number]:IRosterCode} = {};
  payrollRosterCodes: IRosterCode[] = [];
  selectedRosterValue: IRosterValue;
  previousRosterValue: IRosterValue; // introduced to help validate the payroll roster code
  nextRosterValue: IRosterValue; // introduced to help validate the payroll roster code
  selectedRosterCode: IRosterCode = null;
  selectedPayrollRosterCode: IRosterCode = null;
  rosterNote: IRosterNote = null;

  ticketStatuses: any = [
    {id: TicketStatus.Approved, name: "Approved"},
    {id: TicketStatus.PendingApproval, name: "Pending approval"},
    {id: TicketStatus.Planned, name: "Planned"}];

  fromDate: Date;
  toDate: Date;

  private oldRosterValue: IRosterValue;
  private employeeId: number;

  constructor(
    private _dialogRef: DynamicDialogRef,
    private _dialogConfig: DynamicDialogConfig,
    private _patternParser: RosterPatternParser,
    private _payrollRosterCodeRecommendor: PayrollRosterCodeRecommendor,
    private _rosterCodeService: RosterCodeService,
    private _confirmationService: ConfirmationService,
    private _util: PpsUtilityService) {
  }

  ngOnInit(): void {
    this.setData(this._dialogConfig.data);
  }

  setData(rosterData:any){
    this.fromDate = this.toDate = rosterData.date as Date;

    this.employeeName = rosterData.employeeName;
    this.employeeId = rosterData.employeeId;
    this.rosterCodes = _.cloneDeep(rosterData.rosterCodes);
    this.rosterCodes.forEach(rc => this.rosterCodesObject[rc.id] = rc);
    this.payrollRosterCodes = _.cloneDeep(this.rosterCodes.filter(rc=>rc.canBeUsedInTimesheetBreakdownRule));
    
    this.selectedRosterValue = _.cloneDeep(rosterData.selectedRosterValue);
    this.selectedRosterValue.rn = this.selectedRosterValue.rn || {employeeId: this.employeeId, day: this.fromDate, note: ""};

    this.previousRosterValue = rosterData.previousRosterValue;
    this.nextRosterValue = rosterData.nextRosterValue;

    this.oldRosterValue = _.cloneDeep(rosterData.selectedRosterValue);
    this.oldRosterValue.rn = this.oldRosterValue.rn || {employeeId: this.employeeId, day: this.fromDate, note: ""};

    this.showRosterNoteField = this.selectedRosterValue.rc.id !== 0;

    // add special clear roster codes
    this.rosterCodes.unshift({id: -1, code: "Empty"});

    this.checkPayrollRosterCode();
  }

  onRosterCodeChanged(evt){
    if (!evt.value){
      return;
    }

    let selectedRosterCode = evt.value;

    this.errorMessage = "";

    if (!this.showDayNo){
      this.selectedRosterValue.sn = null;
    }

    if (!this.selectedRosterValue.rc.hasTicketStatus){
      this.selectedRosterValue.ts.id = 0;
    }

    this.checkPayrollRosterCode();
  }


  get showDayNo(): boolean{
    return this.selectedRosterValue && 
      this.selectedRosterValue.rc &&
      (this.selectedRosterValue.rc.id === RosterCodes.Day || 
       this.selectedRosterValue.rc.id === RosterCodes.Night);
  }

  showPayrollRosterCode(){
    return this.selectedRosterValue && 
      this.rosterCodesObject.hasOwnProperty(this.selectedRosterValue.rc.id) && 
      !this.rosterCodesObject[this.selectedRosterValue.rc.id]?.canBeUsedInTimesheetBreakdownRule;
  }

  showTicketStatus(){
    return this.selectedRosterValue &&
      this.rosterCodesObject.hasOwnProperty(this.selectedRosterValue.rc.id) && 
      this.rosterCodesObject[this.selectedRosterValue.rc.id]?.hasTicketStatus;
  }

  save(form: NgForm, section){
    this.errorMessage = "";
    
    if (form.invalid){
      return false;
    }

    if (section === 1){
      let result: IApiResult<IEmployeeRosterUpdateModel[]> = this._patternParser.parse(0, new Date(), this.rosterPattern, this.rosterCodesObject);
      if (!result.succeed){
        this.errorMessage = result.errors.join(".");
        return;
      }

      if (isNaN(this.cycles) || +this.cycles < 1){
        this.errorMessage = "There must be at least one cycle";
        return;
      }
    }

      // Check sequence value
    if (this.showDayNo && 
      (!this.selectedRosterValue.sn ||
      isNaN(this.selectedRosterValue.sn))){
      this.errorMessage = "Please enter a day no."
      return false;
    }

    if (this.selectedRosterValue.rc?.canBeUsedInTimesheetBreakdownRule){
      this.selectedRosterValue.prc = this._util.deepCopy(this.selectedRosterValue.rc);
    }

    if (this.selectedRosterValue.rc?.id === 0){
      this.errorMessage = "You must select a roster code."
      return false;
    }

    if (this.selectedRosterValue.rc?.hasTicketStatus && this.selectedRosterValue.ts?.id === 0){
      this.errorMessage = "Please select a ticket status."
      return false;
    }

    // User selected Empty 
    if (this.selectedRosterValue.rc?.id === -1){
      this.selectedRosterValue = {
        rc: {id: 0, code: null},
        prc: {id: 0, code: null},
        sn: null,
        ts: {id: 0, description: null},
        rn: {employeeId: this.employeeId, day: this.fromDate, note: ""}
      };
    }

    if (this.selectedRosterValue.rn){
      this.selectedRosterValue.rn.note = this.selectedRosterValue.rn.note?.trim();
    }

    let updateSection = this.checkChanges();
    console.log("Update section " + updateSection);

    //return;

    let result = {
      employeeId: this.employeeId,
      section: updateSection,

      /* Following 2 fields are for secontion 1 only */
      pattern: this.rosterPattern,
      cycles: this.cycles,

      /* Following 4 fields are for secontion 2 only */
      oldRosterValue: this.oldRosterValue,
      rosterValue: this.selectedRosterValue,
      from: this.fromDate,
      to: this.toDate,

      /* Following 1 fields are for secontion 3 only */
      rosterNote: this.selectedRosterValue.rn
    };

    this._dialogRef.close(result);
  }

  private checkChanges(): number{
    let oldFromDate = this._dialogConfig.data.date as Date;
    let oldToDate = this._dialogConfig.data.date as Date;
    let updateSection = 0;
   

    if (this.selectedRosterValue.rc?.id !== this.oldRosterValue.rc?.id ||
      this.selectedRosterValue.sn !== this.oldRosterValue.sn ||
      this.selectedRosterValue.prc?.id !== this.oldRosterValue.prc?.id ||
      this.selectedRosterValue.ts?.id !== this.oldRosterValue.ts?.id ||
      !isSameDay(this.fromDate, oldFromDate) ||
      !isSameDay(this.toDate, oldToDate)){
        updateSection = 1;
    }

    if (this.selectedRosterValue.rn?.note !== this.oldRosterValue.rn?.note){
      updateSection = updateSection | 2;
    }

    return updateSection;      
  }

  cancel(){
    this._dialogRef.close();
  }

  checkPayrollRosterCode(){
    // checking existing payroll roster code
    if (this.selectedRosterValue.rc.id === 0){
      return;
    }
    
    let recommentedPayrollRosterCode = 
    this._rosterCodeService.getRosterCode(
      this._payrollRosterCodeRecommendor.recommend(this.selectedRosterValue, this.previousRosterValue, this.nextRosterValue)
    );

    if (recommentedPayrollRosterCode?.id !== this.selectedRosterValue.prc?.id){
      this._confirmationService.confirm({
        header: "Incorrect Payroll Roster Code",
        message: `The current payroll roster code is ${this.selectedRosterValue.prc?.code || "not set"}. We recommend changing to ${recommentedPayrollRosterCode?.code}`,
        icon: "pi pi-ban text-danger",
        key: "invalidPayrollRosterCodePromptDialog",
        acceptVisible: true,
        acceptLabel: "Accept Change",
        rejectLabel: "Ignore Change",
        accept: () => {
          this.selectedRosterValue.prc = recommentedPayrollRosterCode;
        }
        //rejectVisible: false
      });
    }
  }
}
